import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { WithAuth } from "../../components/with-auth";
import MainWrapper from "../../layout/MainWrapper";
import { createAxiosInstance } from "../../api/ApiRequest";
import {
    Avatar, Button, Col, Descriptions, Empty, Flex, Popover, Row, Spin, Tabs, Tag, Tooltip, Typography, Badge,
} from 'antd';
import {
    CopyOutlined, LinkOutlined, ReloadOutlined, WalletOutlined, SendOutlined,
    InfoCircleOutlined, WarningOutlined,
} from '@ant-design/icons';
import { notifyError, notifySuccess } from '../../utils/toast';
import { useServerContext } from "../../context/ServerContext";
import { useAuth } from "../../context/AuthContext";
import { ActiveCoinData, BoostTypePublic } from "../../types/botServer";
import { getActiveCoinDexImage } from '../../utils/dexImage';
import { resolveTokenLabel } from '../../utils/tokenLabel';

const EMPTY_BOOST_TYPES: BoostTypePublic[] = [];
const ENCRYPTED_WALLET_LABEL = 'Encrypted';

const truncate = (value: string, max = 12) =>
    value.length > max ? `${value.slice(0, max)}…` : value;

const avatarColor = (seed: string) => {
    const palette = ['#1677ff', '#13c2c2', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96'];
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
};

const copyText = async (text: string, label: string) => {
    try {
        await navigator.clipboard.writeText(text);
        notifySuccess(`${label} copied`);
    } catch {
        notifyError('Copy failed');
    }
};

const buildSubtitle = (coin: ActiveCoinData): string => {
    const parts: string[] = [];
    if (coin.username) parts.push(`@${coin.username.replace('@', '')}`);
    if (coin.wallet && coin.wallet !== ENCRYPTED_WALLET_LABEL) {
        parts.push(truncate(coin.wallet, 8));
    } else if (coin.wallet === ENCRYPTED_WALLET_LABEL) {
        parts.push(ENCRYPTED_WALLET_LABEL);
    }
    return parts.join(' · ');
};

const buildMeta = (coin: ActiveCoinData): string => {
    const dexPool = [coin.dex, coin.pool_type].filter(Boolean).join('-');
    const parts = [coin.platform, dexPool, coin.pool_id].filter(Boolean);
    return parts.join(' · ');
};

const ActionIcon: React.FC<{
    title: string;
    icon: React.ReactNode;
    onClick?: () => void;
    href?: string;
    danger?: boolean;
}> = ({ title, icon, onClick, href, danger }) => {
    const className = `active-coin-action${danger ? ' active-coin-action-danger' : ''}`;

    if (href) {
        return (
            <Tooltip title={title}>
                <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
                    {icon}
                </a>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={title}>
            <button type="button" className={className} onClick={onClick}>
                {icon}
            </button>
        </Tooltip>
    );
};

const CoinDetailPopover: React.FC<{ coin: ActiveCoinData }> = ({ coin }) => {
    const fullName = [coin.firstname, coin.lastname].filter(Boolean).join(' ');
    const walletDisplay = coin.wallet || '—';

    const items = [
        { label: 'Order Wallet', children: walletDisplay },
        { label: 'Symbol', children: resolveTokenLabel(coin.token_symbol, undefined, coin.token_address) },
        { label: 'Token Address', children: coin.token_address || '—' },
        { label: 'Pool', children: coin.pool_id || '—' },
        { label: 'Dex', children: [coin.dex, coin.pool_type].filter(Boolean).join(' · ') || '—' },
        { label: 'User', children: fullName || '—' },
        { label: 'Telegram', children: coin.username ? `@${coin.username.replace('@', '')}` : '—' },
        { label: 'Platform', children: coin.platform || '—' },
    ].filter((item) => item.children !== '—');

    return (
        <Descriptions
            size="small"
            column={1}
            className="active-coin-popover-desc"
            items={items.map((item) => ({ key: item.label, label: item.label, children: item.children }))}
        />
    );
};

const CoinRow: React.FC<{
    coin: ActiveCoinData;
    footerLayout?: 'stacked' | 'inline';
}> = ({ coin, footerLayout = 'stacked' }) => {
    const [dexImgError, setDexImgError] = useState(false);
    const tokenSymbol = resolveTokenLabel(coin.token_symbol, undefined, coin.token_address);
    const { label: dexLabel, src: dexImageSrc } = getActiveCoinDexImage(coin);
    const subtitle = buildSubtitle(coin);
    const meta = buildMeta(coin);
    const dexLink = coin.pool_id
        ? `https://dexscreener.com/solana/${coin.pool_id}`
        : coin.token_address
            ? `https://dexscreener.com/solana/${coin.token_address}`
            : undefined;
    const walletValid = Boolean(coin.wallet && coin.wallet !== ENCRYPTED_WALLET_LABEL);

    return (
        <div className={`active-coin-row${coin.error ? ' active-coin-row-error' : ''}`}>
            <div className="active-coin-row-avatar-wrap">
                {dexImageSrc && !dexImgError ? (
                    <img
                        src={dexImageSrc}
                        alt={dexLabel || 'DEX'}
                        className="active-coin-row-avatar active-coin-row-dex-avatar"
                        onError={() => setDexImgError(true)}
                    />
                ) : (
                    <Avatar
                        size={32}
                        style={{ backgroundColor: avatarColor(dexLabel || tokenSymbol), fontSize: 11, fontWeight: 600 }}
                    >
                        {(dexLabel || tokenSymbol).slice(0, 2).toUpperCase()}
                    </Avatar>
                )}
            </div>

            <div className="active-coin-row-body">
                <Flex align="center" gap={6} wrap="wrap" className="active-coin-row-head">
                    <span className="active-coin-row-name" title={tokenSymbol}>{truncate(tokenSymbol, 14)}</span>
                    {coin.error && (
                        <Tooltip title={coin.error}>
                            <Tag color="error" icon={<WarningOutlined />}>Error</Tag>
                        </Tooltip>
                    )}
                </Flex>

                {subtitle && (
                    <div className="active-coin-row-sub" title={subtitle}>{subtitle}</div>
                )}

                <div className={`active-coin-row-footer active-coin-row-footer--${footerLayout}`}>
                    {meta && (
                        <div className="active-coin-row-meta" title={meta}>{meta}</div>
                    )}
                    <Flex align="center" gap={3} className="active-coin-row-actions">
                        {walletValid && (
                            <>
                                <ActionIcon
                                    title="Copy order wallet"
                                    icon={<WalletOutlined />}
                                    onClick={() => copyText(coin.wallet, 'Wallet')}
                                />
                                <ActionIcon
                                    title="Solscan wallet"
                                    icon={<LinkOutlined />}
                                    href={`https://solscan.io/account/${coin.wallet}`}
                                />
                            </>
                        )}
                        {coin.token_address && (
                            <ActionIcon
                                title="Copy token address"
                                icon={<CopyOutlined />}
                                onClick={() => copyText(coin.token_address, 'Token')}
                            />
                        )}
                        {dexLink && (
                            <ActionIcon
                                title="DexScreener"
                                icon={<LinkOutlined />}
                                href={dexLink}
                            />
                        )}
                        {coin.username && (
                            <ActionIcon
                                title="Telegram"
                                icon={<SendOutlined />}
                                href={`https://t.me/${coin.username.replace('@', '')}`}
                            />
                        )}
                        <Popover
                            trigger="click"
                            placement="rightTop"
                            title={tokenSymbol}
                            overlayClassName="active-coin-detail-popover"
                            content={<CoinDetailPopover coin={coin} />}
                        >
                            <span>
                                <ActionIcon title="Details" icon={<InfoCircleOutlined />} />
                            </span>
                        </Popover>
                    </Flex>
                </div>
            </div>
        </div>
    );
};

const ActiveCoin: React.FC = () => {
    const history = useNavigate();
    const { logout } = useAuth();
    const axiosInstance = useMemo(() => createAxiosInstance(history, logout), [history, logout]);
    const { selectedServer, loading: configLoading } = useServerContext();
    const intervalTime = 120000;

    const serverId = selectedServer?.serverId ?? '';
    const boostTypes = selectedServer?.boostTypes ?? EMPTY_BOOST_TYPES;
    const boostTypeKey = boostTypes.map((b) => b.id).join(',');

    const [coinsByType, setCoinsByType] = useState<Record<string, ActiveCoinData[]>>({});
    const [countsByType, setCountsByType] = useState<Record<string, number>>({});
    const [loadingByType, setLoadingByType] = useState<Record<string, boolean>>({});
    const [fetchTick, setFetchTick] = useState(0);
    const fetchGenRef = useRef(0);

    const totalActive = Object.values(countsByType).reduce((a, b) => a + b, 0);

    const fetchBoostType = useCallback(async (targetServerId: string, boostType: BoostTypePublic) => {
        if (!targetServerId || !boostType?.id) return;

        const generation = fetchGenRef.current;
        setLoadingByType((prev) => ({ ...prev, [boostType.id]: true }));
        try {
            const res = await axiosInstance.post('/api/v1/get_server_active_coins', {
                serverId: targetServerId,
                boostTypeId: boostType.id,
            });
            if (generation !== fetchGenRef.current) return;

            if (res.data?.flag) {
                setCoinsByType((prev) => ({ ...prev, [boostType.id]: res.data.tokens }));
                setCountsByType((prev) => ({ ...prev, [boostType.id]: res.data.totalNum }));
            } else {
                notifyError(res.data?.message || 'Fetch failed');
            }
        } catch {
            if (generation === fetchGenRef.current) {
                notifyError('Failed to fetch active coins');
            }
        } finally {
            if (generation === fetchGenRef.current) {
                setLoadingByType((prev) => ({ ...prev, [boostType.id]: false }));
            }
        }
    }, [axiosInstance]);

    const fetchAll = useCallback((targetServerId: string, types: BoostTypePublic[]) => {
        if (!targetServerId || types.length === 0) return;
        for (const boost of types) {
            if (boost?.id) fetchBoostType(targetServerId, boost);
        }
    }, [fetchBoostType]);

    useEffect(() => {
        if (configLoading || !serverId || boostTypes.length === 0) return;
        fetchGenRef.current += 1;
        setCoinsByType({});
        setCountsByType({});
        setLoadingByType({});
        fetchAll(serverId, boostTypes);
    }, [configLoading, serverId, fetchTick, fetchAll, boostTypeKey]);

    useEffect(() => {
        if (configLoading || !serverId) return;
        const timer = setTimeout(() => setFetchTick((t) => t + 1), intervalTime);
        return () => clearTimeout(timer);
    }, [configLoading, serverId, fetchTick, intervalTime]);

    if (configLoading) {
        return (
            <WithAuth><MainWrapper><Spin tip="Loading..." /></MainWrapper></WithAuth>
        );
    }

    if (!selectedServer) {
        return (
            <WithAuth><MainWrapper><Empty description="No server selected" /></MainWrapper></WithAuth>
        );
    }

    const renderColumn = (boost: BoostTypePublic) => {
        const coins = coinsByType[boost.id] ?? [];
        const count = countsByType[boost.id] ?? 0;
        const loading = loadingByType[boost.id] ?? true;

        return (
            <Col xs={24} sm={12} lg={8} xl={6} xxl={4} key={boost.id}>
                <div className="active-coin-column">
                    <Flex align="center" justify="space-between" className="active-coin-column-header">
                        <Flex align="center" gap={8}>
                            <span className="active-coin-column-title">{boost.label}</span>
                            <Badge count={count} overflowCount={999} showZero color="#1677ff" />
                        </Flex>
                        <Button
                            type="text"
                            size="small"
                            icon={<ReloadOutlined />}
                            loading={loading}
                            className="active-coin-column-reload"
                            onClick={() => fetchBoostType(serverId, boost)}
                        />
                    </Flex>
                    <div className="active-coin-column-body">
                        {loading && coins.length === 0 && <Spin className="active-coin-column-spinner" />}
                        {!loading && coins.length === 0 && (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No active" />
                        )}
                        {coins.map((coin) => (
                            <CoinRow key={coin._id} coin={coin} />
                        ))}
                    </div>
                </div>
            </Col>
        );
    };

    const tabItems = [
        {
            key: 'all',
            label: <span>All <Badge count={totalActive} overflowCount={999} /></span>,
            children: <Row gutter={[8, 8]}>{boostTypes.map(renderColumn)}</Row>,
        },
        ...boostTypes.map((boost) => ({
            key: boost.id,
            label: <span>{boost.label} <Badge count={countsByType[boost.id] ?? 0} overflowCount={99} /></span>,
            children: (
                <div className="active-coin-list">
                    {(coinsByType[boost.id] ?? []).map((coin) => (
                        <CoinRow
                            key={coin._id}
                            coin={coin}
                            footerLayout="inline"
                        />
                    ))}
                    {!loadingByType[boost.id] && (coinsByType[boost.id] ?? []).length === 0 && (
                        <Empty description="No active orders" />
                    )}
                </div>
            ),
        })),
    ];

    return (
        <WithAuth>
            <MainWrapper>
                <Flex align="center" justify="space-between" className="active-coin-toolbar">
                    <div>
                        <div className="dashboard-table-title">Active Orders</div>
                        <Typography.Text type="secondary">
                            {selectedServer.name} · {totalActive} active
                        </Typography.Text>
                    </div>
                    <Button icon={<ReloadOutlined />} onClick={() => setFetchTick((t) => t + 1)}>
                        Refresh
                    </Button>
                </Flex>
                <Tabs defaultActiveKey="all" items={tabItems} className="active-coin-tabs" />
            </MainWrapper>
        </WithAuth>
    );
};

export default ActiveCoin;
